import React from 'react';
import { Headline, Layout, LayoutProps, Text, TextButton } from '@oneaudi/unified-web-components';
import {
  SYS_BREAKPOINT_MD,
  SYS_SPACE_FIXED_300,
  SYS_SPACE_FIXED_500,
} from '@oneaudi/unified-web-common';
import { useI18n } from '@oneaudi/i18n-context';
import { ACTIONS, SELECTORS, StockcarsStoreClass } from '@oneaudi/stck-store';
import { useDispatch } from 'react-redux';
import {
  useServicesContext,
  generateFilterRequestParam,
  fetchResults,
  useDesktopOrMobileView,
  dynamicStylingHelper,
} from '@oneaudi/vtp-shared';
import { AppContextForThisInstance, getAppContentConfiguration } from '../../hooks/tileHook';
import { StyledTextButton } from './ZeroResultsPage.styles';
import styled from 'styled-components';

interface ZeroResultsProps {
  context: AppContextForThisInstance;
}

const ZeroResultsPage: React.FC<ZeroResultsProps> = ({ context }: ZeroResultsProps) => {
  const { newSearchUrl, vtpConfiguration } = getAppContentConfiguration();

  const zeroResultsHeadline = useI18n({
    id: 'nemo.ui.sc.result.noresultstext',
    defaultMessage: '',
  });
  const resetFiltersLabel = useI18n({
    id: 'nemo.ui.sc.filter-options.reset.label',
    defaultMessage: '',
  });
  const zeroFavsHeadline = useI18n({
    id: 'nemo.ui.sc.favorites.empty.headline',
    defaultMessage: '',
  });

  const zeroFavsInfo = useI18n({
    id: 'nemo.ui.sc.favorites.empty.copy',
    defaultMessage: '',
  });
  const newSearchLabel = useI18n({
    id: 'nemo.ui.sc.favorites.empty.newsearch',
    defaultMessage: '',
  });
  const dispatch = useDispatch();
  const { getEnvironmentConfig } = useServicesContext();

  const store = StockcarsStoreClass.getInstance('', '');
  const presets = SELECTORS.FILTERS.getPersistedPresetsMap(store.state);
  const sorting = SELECTORS.SORTING.getSortingStateRaw(store.state);

  const requestParam = generateFilterRequestParam(new Map(), presets);
  requestParam.set('size', 12);
  requestParam.set('sort', sorting.results);

  const clearAllFilters = async () => {
    const response = await fetchResults(
      getEnvironmentConfig()!.scs.baseUrl,
      vtpConfiguration.fields.scs_scsMarketPath,
      requestParam,
      getEnvironmentConfig()!.scs.apiKey
    );
    dispatch(ACTIONS.FILTERS.handleFilterResponse({ response }));
    // clean the legacy filter chips
    document.querySelector('.sc-md-filter-options')?.remove();
  };
  const isMobile: boolean = useDesktopOrMobileView(SYS_BREAKPOINT_MD) === 'mobile';
  const headline = context === 'favorites' ? zeroFavsHeadline : zeroResultsHeadline;

  return (
    <>
      {context === 'results' && (
        <>
          <Text
            tag="p"
            variant={{ order: '1', style: 'normal', type: 'copy' }}
            data-testid="zeroResultsHeadline"
            margin={[0, 0, SYS_SPACE_FIXED_500, 0]}
          >
            {headline}
          </Text>
          <StyledTextButton>
            <TextButton
              variant="primary"
              size="md"
              onClick={clearAllFilters}
              data-testid="reset"
              isFullWidth={isMobile}
            >
              {resetFiltersLabel}
            </TextButton>
          </StyledTextButton>
        </>
      )}
      {context === 'favorites' && (
        <StyledLayout direction="column" justify="center" margin={[SYS_SPACE_FIXED_500, 0]}>
          <Headline
            tag="h3"
            variant={{ order: '3', weight: 'normal', type: 'headline' }}
            data-testid="zeroResultsHeadline"
            margin={[0, 0, SYS_SPACE_FIXED_300, 0]}
          >
            {headline}
          </Headline>
          <Text
            tag="p"
            variant={{ order: '1', style: 'normal', type: 'copy' }}
            data-testid="zeroFavsHeadline"
            margin={[0, 0, SYS_SPACE_FIXED_500, 0]}
          >
            {zeroFavsInfo}
          </Text>
          <StyledTextButton>
            <TextButton variant="primary" href={newSearchUrl} size="md">
              {newSearchLabel}
            </TextButton>
          </StyledTextButton>
        </StyledLayout>
      )}
    </>
  );
};

export default ZeroResultsPage;

export const StyledLayout = styled((props) => <Layout {...props} />)<LayoutProps>`
  max-width: ${dynamicStylingHelper('unset', '740px')};
`;
